/**
 * Numeric codes reported by the physical SSBD device via `unsolicitedMessage`,
 * `statusChange`, and `statusIsOK` events.
 *
 * These arrive in the SD (statusDevice) or BS (bagStatus) fields:
 * - 139 = Emergency button pressed
 * - 302 = Emergency unlocked / hardware error
 * - 305 = Device not responding (triggers OOS)
 * - 122 = Diagnostic reset (clears emergency state)
 */
export enum SsbdEmergencyCode {
  /** Emergency button pressed — belt stopped. Opens modal immediately, no OOS. */
  EMERGENCY_PRESSED = 139,
  /** Emergency unlocked / hardware error — requires threshold confirmation (3 consecutive). No OOS. */
  EMERGENCY_UNLOCKED = 302,
  /** Device not responding — hardware offline. Opens modal immediately + OOS. */
  NOT_RESPONDING = 305,
  /** Diagnostic reset — emergency condition cleared. */
  DIAGNOSTIC_RESET = 122,
}

/**
 * Codes that represent an active error condition (the modal should NOT be closed while
 * any of these is the current device status).
 *
 * Behavior per code:
 * - 139: opens modal immediately on first occurrence, by any channel.
 * - 305: opens modal immediately + triggers OOS, by any channel.
 * - 302: opens modal only after SSBD_HARDWARE_ERROR_THRESHOLD consecutive observations
 *         via `statusChange` or `statusIsOK`. Never triggers OOS.
 *         If 139 was received first, 302 keeps the modal open (already shown).
 */
export const SSBD_EMERGENCY_OPEN_CODES: ReadonlySet<number> = new Set([
  SsbdEmergencyCode.EMERGENCY_PRESSED,
  SsbdEmergencyCode.EMERGENCY_UNLOCKED,
  SsbdEmergencyCode.NOT_RESPONDING,
]);

/** All codes that explicitly close the emergency modal. */
export const SSBD_EMERGENCY_CLOSE_CODES: ReadonlySet<number> = new Set([
  SsbdEmergencyCode.DIAGNOSTIC_RESET,
]);

/**
 * Number of consecutive 302 observations (via `statusChange` or `statusIsOK`)
 * required before opening the emergency modal, when 139 was NOT received first.
 *
 * With the poll interval at 30s (idle) / 60s (active), reaching the threshold
 * takes approximately 60–120 seconds for a persistent hardware error.
 */
export const SSBD_HARDWARE_ERROR_THRESHOLD = 4;
